import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Download,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  FileArchive,
  Loader2,
} from 'lucide-react';
import { CSV_DELIMITER_OPTIONS, FILE_LABELS, type LogicalFileSpec } from '../stepCatalog';
import type { CsvDelimiter } from '../types';
import { downloadTemplates } from '../api';
import { detectZipBundle, type ZipDetectionResult } from '../zipBundle';
import { detectWorkbookBundle, workbookSheetToCsvFile } from '../workbookBundle';

interface FileUploaderProps {
  code: string;
  requiredFiles: LogicalFileSpec[];
  files: Record<string, File>;
  onFilesChange: (files: Record<string, File>) => void;
  /**
   * Séparateur détecté dans les fichiers déposés (les exports Google Sheets
   * sont en virgules, nos modèles en « ; ») : affiché ici pour que la lecture
   * réelle des fichiers ne soit jamais une surprise.
   */
  detectedDelimiter?: CsvDelimiter | null;
}

/**
 * Collecte les fichiers localement (pas d'upload immédiat vers V2) : ils sont
 * analysés à l'étape « Réconciliation » puis préparés/uploadés à l'étape
 * « Aperçu ».
 *
 * Deux modes : un bundle unique — archive ZIP de CSV ou classeur Excel .xlsx
 * (un onglet = un fichier logique) — avec détection automatique des fichiers
 * présents, ou un envoi fichier par fichier (.csv ou .xlsx). Les classeurs
 * sont convertis en CSV côté navigateur : le reste de la chaîne ne voit que
 * du CSV (délimiteur « ; », le défaut de la configuration).
 */
export function FileUploader({
  code,
  requiredFiles,
  files,
  onFilesChange,
  detectedDelimiter,
}: FileUploaderProps) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (requiredFiles.length === 0) {
    return (
      <Alert>
        <CheckCircle2 className="h-4 w-4" />
        <AlertDescription>
          Les éléments sélectionnés ne nécessitent aucun fichier. Vous pouvez passer à l’étape
          suivante.
        </AlertDescription>
      </Alert>
    );
  }

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      await downloadTemplates(
        code,
        requiredFiles.map((f) => f.logicalName),
      );
    } catch {
      setError('Échec du téléchargement des modèles.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Téléchargez les modèles (en-têtes attendus), remplissez-les, puis chargez-les via une
          archive ZIP, un classeur Excel (un onglet par fichier) ou un par un.
        </p>
        <Button variant="outline" onClick={handleDownload} disabled={downloading}>
          <Download className="mr-2 h-4 w-4" />
          {downloading ? 'Génération…' : 'Télécharger les modèles'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {detectedDelimiter && Object.keys(files).length > 0 && (
        <Alert>
          <FileSpreadsheet className="h-4 w-4" />
          <AlertDescription>
            Séparateur détecté dans vos fichiers :{' '}
            <strong>
              {CSV_DELIMITER_OPTIONS.find((o) => o.value === detectedDelimiter)?.label ??
                detectedDelimiter}
            </strong>{' '}
            — c’est celui qui sera utilisé pour les lire.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="bundle">
        <TabsList>
          <TabsTrigger value="bundle">
            <FileArchive className="mr-2 h-4 w-4" />
            Archive ZIP / Classeur Excel
          </TabsTrigger>
          <TabsTrigger value="single">
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Fichier par fichier
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bundle" className="pt-4">
          <BundleMode requiredFiles={requiredFiles} files={files} onFilesChange={onFilesChange} />
        </TabsContent>

        <TabsContent value="single" className="pt-4">
          <SingleMode requiredFiles={requiredFiles} files={files} onFilesChange={onFilesChange} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

type ModeProps = Pick<FileUploaderProps, 'requiredFiles' | 'files' | 'onFilesChange'>;

/**
 * Upload d'un bundle : archive ZIP (fichiers reconnus par leur nom) ou
 * classeur Excel (fichiers reconnus par le nom des onglets), puis remplacement
 * du bundle courant par son contenu.
 */
function BundleMode({ requiredFiles, files, onFilesChange }: ModeProps) {
  const [parsing, setParsing] = useState(false);
  const [bundleName, setBundleName] = useState<string | null>(null);
  const [isWorkbook, setIsWorkbook] = useState(false);
  const [result, setResult] = useState<ZipDetectionResult | null>(null);
  const [bundleError, setBundleError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleBundle = async (file: File | null) => {
    if (!file) return;
    const zip = /\.zip$/i.test(file.name);
    const xlsx = /\.xlsx$/i.test(file.name);
    if (!zip && !xlsx) {
      setBundleName(file.name);
      setResult(null);
      setBundleError(
        /\.xls$/i.test(file.name)
          ? 'Format .xls (ancien Excel) non pris en charge : réenregistrez le classeur en .xlsx.'
          : 'Veuillez déposer une archive .zip ou un classeur .xlsx.',
      );
      onFilesChange({});
      return;
    }
    setParsing(true);
    setBundleError(null);
    setResult(null);
    setBundleName(file.name);
    setIsWorkbook(xlsx);
    try {
      const detection = zip
        ? await detectZipBundle(file, requiredFiles)
        : await detectWorkbookBundle(file, requiredFiles);
      setResult(detection);
      const next: Record<string, File> = {};
      for (const entry of detection.matched) {
        next[entry.logicalName] = entry.file;
      }
      onFilesChange(next);
    } catch (e) {
      setBundleError(
        e instanceof Error && e.message
          ? e.message
          : 'Fichier illisible. Vérifiez qu’il s’agit bien d’une archive .zip ou d’un classeur .xlsx valide.',
      );
      onFilesChange({});
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="py-6">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              handleBundle(e.dataTransfer.files?.[0] ?? null);
            }}
            className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed py-8 text-center transition-colors ${
              dragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <FileArchive className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm">
              <span className="font-medium text-primary">Choisir une archive ZIP ou un classeur Excel</span>{' '}
              ou glissez le fichier ici
            </div>
            <p className="text-xs text-muted-foreground">
              ZIP&nbsp;: fichiers reconnus par leur nom (ex&nbsp;: <code>eleves.csv</code>).
              Excel&nbsp;: un onglet par fichier, reconnu par son nom (ex&nbsp;:
              «&nbsp;Élèves&nbsp;»).
            </p>
            <input
              type="file"
              accept=".zip,.xlsx,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handleBundle(e.target.files?.[0] ?? null)}
            />
          </label>
        </CardContent>
      </Card>

      {parsing && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Analyse de « {bundleName} »…
        </div>
      )}

      {bundleError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{bundleError}</AlertDescription>
        </Alert>
      )}

      {result && !parsing && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {isWorkbook ? 'Classeur' : 'Archive'} « {bundleName} » — {result.matched.length}{' '}
            {isWorkbook ? 'onglet(s) reconnu(s)' : 'fichier(s) reconnu(s)'}.
          </p>

          <div className="grid gap-2 sm:grid-cols-2">
            {requiredFiles.map((spec) => {
              const present = files[spec.logicalName];
              return (
                <div
                  key={spec.logicalName}
                  className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <span>
                    {FILE_LABELS[spec.logicalName] ?? spec.logicalName}{' '}
                    <span className="font-mono text-xs text-muted-foreground">
                      ({isWorkbook ? `onglet ${spec.logicalName}` : `${spec.logicalName}.csv`})
                    </span>
                  </span>
                  {present ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Détecté
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <AlertTriangle className="h-4 w-4" />
                      Absent
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {result.ignored.length > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Ignoré(s) (non attendu pour la sélection)&nbsp;: {result.ignored.join(', ')}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}

/** Upload classique, un fichier (.csv ou .xlsx) par type logique. */
function SingleMode({ requiredFiles, files, onFilesChange }: ModeProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [converting, setConverting] = useState<Record<string, boolean>>({});

  const setFieldError = (logicalName: string, message: string | null) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[logicalName] = message;
      else delete next[logicalName];
      return next;
    });
  };

  const handleSelectFile = async (logicalName: string, file: File | null) => {
    setFieldError(logicalName, null);
    if (!file) {
      const next = { ...files };
      delete next[logicalName];
      onFilesChange(next);
      return;
    }
    let resolved = file;
    if (/\.xlsx$/i.test(file.name)) {
      setConverting((prev) => ({ ...prev, [logicalName]: true }));
      try {
        resolved = await workbookSheetToCsvFile(file, logicalName);
      } catch (e) {
        setFieldError(
          logicalName,
          e instanceof Error && e.message ? e.message : 'Classeur illisible.',
        );
        return;
      } finally {
        setConverting((prev) => ({ ...prev, [logicalName]: false }));
      }
    }
    onFilesChange({ ...files, [logicalName]: resolved });
  };

  return (
    <div className="space-y-4">
      {requiredFiles.map((spec) => {
        const file = files[spec.logicalName];
        return (
          <Card key={spec.logicalName}>
            <CardHeader icon={<FileSpreadsheet />} iconColor="green">
              <CardTitle className="text-base">
                {FILE_LABELS[spec.logicalName] ?? spec.logicalName}{' '}
                <span className="font-mono text-sm font-normal text-muted-foreground">
                  ({spec.logicalName}.csv ou .xlsx)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {Object.values(spec.columns).map((header) => (
                  <Badge key={header} variant="outline" className="text-xs font-mono">
                    {header}
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => handleSelectFile(spec.logicalName, e.target.files?.[0] ?? null)}
                  className="block text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                />
                {converting[spec.logicalName] && (
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Conversion…
                  </span>
                )}
                {file && !converting[spec.logicalName] && (
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    {file.name}
                  </span>
                )}
              </div>
              {errors[spec.logicalName] && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{errors[spec.logicalName]}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
